import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import TotalAmountCard from '../components/common/TotalAmountCard';
import { usePageTitle } from '../context/PageTitleContext';
import './PurchaseHistory.scss';

function PurchaseHistory() {
  const { user } = useSelector(state => state.auth);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { setPageTitle } = usePageTitle();

  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Purchase History");
  }, [setPageTitle]);

  useEffect(() => {
    fetchPurchases();
  }, [user]);

  const fetchPurchases = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      // Get all orders
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const purchasesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by date (newest first)
      purchasesData.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setPurchases(purchasesData);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPurchases = () => {
    if (filter === 'all') return purchases;
    return purchases.filter(purchase => purchase.status === filter);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'processing_payment':
        return 'Processing Payment';
      case 'failed':
        return 'Failed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'pending':
        return 'pending';
      case 'processing_payment':
        return 'processing';
      case 'failed':
        return 'failed';
      default:
        return '';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="purchase-history-page">
      {!loading && purchases.length > 0 && (
        <div className="purchase-summary">
          <TotalAmountCard orders={purchases} title="Lifetime Spending" />
        </div>
      )}
      
      <div className="filter-controls">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
          <button 
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button 
            className={`filter-btn ${filter === 'failed' ? 'active' : ''}`}
            onClick={() => setFilter('failed')}
          >
            Failed
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">Loading your purchase history...</div>
      ) : getFilteredPurchases().length > 0 ? (
        <div className="purchases-table">
          <div className="purchases-header">
            <div className="col date">Date</div>
            <div className="col">Order ID</div>
            <div className="col">Package</div>
            <div className="col">Quantity</div>
            <div className="col amount">Amount</div>
            <div className="col">Status</div>
          </div>
          
          <div className="purchases-list">
            {getFilteredPurchases().map(purchase => (
              <div key={purchase.id} className="purchase-row">
                <div className="col date">
                  {formatDate(purchase.createdAt)}
                </div>
                <div className="col order-id">
                  #{purchase.id.slice(-6).toUpperCase()}
                </div>
                <div className="col package-title">
                  {purchase.packageTitle}
                </div>
                <div className="col quantity">
                  {purchase.quantity}
                </div>
                <div className="col amount">
                  ${purchase.totalPrice}
                </div>
                <div className="col status">
                  <span className={`status-badge ${getStatusClass(purchase.status)}`}>
                    {getStatusLabel(purchase.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No purchases found</h3>
          <p>
            {filter === 'all' 
              ? "You haven't made any purchases yet." 
              : `You don't have any ${filter} purchases.`}
          </p>
          {filter !== 'all' ? (
            <button 
              className="btn btn-secondary" 
              onClick={() => setFilter('all')}
            >
              View All Purchases
            </button>
          ) : (
            <a href="/packages" className="btn btn-primary">Browse Packages</a>
          )}
        </div>
      )}
    </div>
  );
}

export default PurchaseHistory; 